import { useId } from "react";
import { Activity } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { OverviewEventTimelineProps } from "./interfaces/interface";
import { Skeleton } from "../interfaces/CommonInterfaces";
import OverviewCardHeader from "./OverviewCardHeader";
import OverviewEmptyState from "./OverviewEmptyState";

export default function OverviewEventTimeline({
  fromdetails = false,
  timeline,
  loading,
}: OverviewEventTimelineProps) {
  const noData = !timeline || timeline.length === 0;
  const gradientId = `overview-events-${useId().replace(/:/g, "")}`;

  return (
    <Card className="overview-card overview-timeline-card h-full">
      <OverviewCardHeader
        fromdetails={fromdetails}
        title="Events Timeline"
        description="Recorded event volume over the selected time range"
        icon={Activity}
      />
      <CardContent className="overview-card-content">
        {loading ? (
          <div className="overview-timeline flex flex-col justify-between h-64">
            <Skeleton className="w-full h-48 rounded-md" />
            <div className="flex justify-between mt-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="w-8 h-3" />)}
            </div>
          </div>
        ) : noData ? (
          fromdetails ? (
            <OverviewEmptyState icon={Activity} message="No events found in this time range." />
          ) : (
            <div className="flex items-center justify-center h-64 text-sm font-medium text-muted-foreground">No data found</div>
          )
        ) : (
          <div className="overview-timeline h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline} margin={fromdetails ? { top: 12, right: 12, left: -20, bottom: 0 } : undefined}>
                {fromdetails && (
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                )}
                <CartesianGrid
                  strokeDasharray={fromdetails ? "4 6" : "3 3"}
                  vertical={fromdetails ? false : undefined}
                  stroke={fromdetails ? "hsl(var(--border))" : undefined}
                  className={fromdetails ? "opacity-60" : "opacity-30"}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: fromdetails ? 11 : 10, ...(fromdetails && { fill: "hsl(var(--muted-foreground))" }) }}
                  tickMargin={fromdetails ? 12 : undefined}
                  className="text-muted-foreground"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: fromdetails ? 11 : 10, ...(fromdetails && { fill: "hsl(var(--muted-foreground))" }) }}
                  className="text-muted-foreground"
                />
                <Tooltip
                  wrapperClassName={fromdetails ? "overview-chart-tooltip" : undefined}
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(value) => [value, "Events"]}
                />
                <Area
                  type="monotone"
                  dataKey="events"
                  stroke="hsl(var(--primary))"
                  fill={fromdetails ? `url(#${gradientId})` : "hsl(var(--primary) / 0.2)"}
                  strokeWidth={fromdetails ? 2.5 : 2}
                  activeDot={fromdetails ? { r: 5, stroke: "hsl(var(--card))", strokeWidth: 3 } : undefined}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
